import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import DataGrid, { SortColumn, textEditor } from "react-data-grid";
import {
  ArrowDropDown as IconAsc,
  ArrowDropUp as IconDesc,
} from "@mui/icons-material";

import Btn from "components/Custom/Btn";
import TxtBox from "components/Custom/TxtBox";
import ChkBox from "components/Custom/ChkBox";

import { exportToCsv, exportToPdf } from "./export";
import { LABELS } from "common/lables";
import { COLORS } from "common/colors";

import "react-data-grid/lib/styles.css";
import "./styles.css";

const ExcelGrid = (props: any) => {
  let inputRef: any = useRef<any>({});

  const [filterValue, setFilterValue] = useState<any>("");

  // Context is needed to read filter values otherwise columns are
  // re-created when filters are changed and filter loses focus
  const FilterContext = createContext<any>(undefined);

  const inputStopPropagation = (event: any) => {
    if (["ArrowLeft", "ArrowRight"].includes(event.key)) {
      event.stopPropagation();
    }
  };

  const selectStopPropagation = (event: any) => {
    if (
      ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)
    ) {
      event.stopPropagation();
    }
  };

  const generateCols = (data: []) => {
    return data.map((c: any, i: number) => {
      c.headerCellClass = "excelHeader";
      if (c.key === "id") {
        c.editable = false;
        c.frozen = true;
        c.sortable = true;
        c.headerCellClass = "excelHeader dontprint";
        c.cellClass = "dontprint";
      } else if (c.type === "text" || c.type === "number") {
        c.renderEditCell = textEditor;
      } else if (c.type === "date") {
        c.renderEditCell = (p: any) => (
          <div className="excelDate">
            <TxtBox
              id={p.row.id}
              type="date"
              defaultValue={p.row[c.key]}
              onChange={(e) => {
                p.onRowChange(
                  {
                    ...p.row,
                    [c.key]: e.target.value,
                  },
                  true
                );
              }}
            />
          </div>
        );
      } else if (c.type === "image") {
        c.renderCell = ({ row }: any) => {
          return (
            <div
              className="excelPhoto"
              onClick={() => window.open(row.photo, "new")}
            >
              <img width={"50%"} src={row.photo} className={""} />
            </div>
          );
        };
      } else if (c.type === "dropdown") {
        c.renderEditCell = (p: any) => {
          return (
            <select
              autoFocus={true}
              className={"excelDropDown"}
              value={p.row[c.key]}
              onChange={(e) => {
                p.onRowChange(
                  {
                    ...p.row,
                    [c.key]: c.list[e.target.selectedIndex].label,
                  },
                  true
                );
              }}
            >
              {c.list.map((d: any) => (
                <option key={d.id} value={d.id}>
                  {d.label}
                </option>
              ))}
            </select>
          );
        };
      } else if (c.type === "boolean") {
        c.renderCell = ({ row, onRowChange, tabIndex }) => {
          return (
            <div className="excelChkWrapper">
              <ChkBox
                checked={row[c.key]}
                disabled={c.editable === false}
                onChange={(val: any) => {
                  onRowChange({ ...row, [c.key]: !row[c.key] });
                }}
              />
            </div>
          );
        };
      } else if (c.key === "options") {
        c.cellClass = "dontprint";
        c.headerCellClass = "excelHeader dontprint";
      } else if (c.type === "htmltext") {
        c.renderCell = ({ row, onRowChange, tabIndex }) => {
          return (
            <div
              className="html"
              dangerouslySetInnerHTML={{ __html: row[c.key] }}
            />
          );
        };
      }
      if (c.className) {
        c.headerCellClass += " " + c.className;
      }
      if (c.searchable) {
        c.renderHeaderCell = (p: any) => {
          return (
            <div
              className="flexColumn"
              style={{ width: "100%", position: "relative" }}
            >
              <label
                style={{
                  marginBottom: "-0.9rem",
                  marginTop: "-1.1rem",
                  cursor: p.column.sortable ? "pointer" : "default",
                }}
                onClick={() => {
                  if (!p.column.sortable) {
                    return;
                  }
                  let sortObj: any = [
                    {
                      columnKey: p.column.key,
                      direction: "ASC",
                    },
                  ];
                  if (sortColumns.length > 0) {
                    if (sortColumns[0].direction === "ASC") {
                      sortObj[0].direction = "DESC";
                    } else {
                      sortObj = [];
                    }
                  }
                  onSortColumnsChange(sortObj);
                }}
              >
                {p.column.name}
              </label>
              {p.column.searchable &&
                (p.column.type === "text" ||
                  p.column.type === "number" ||
                  p.column.type === "date") && (
                  <input
                    ref={(ref) => (inputRef[p.column.key] = ref)}
                    key={p.column.key}
                    tabIndex={0}
                    autoFocus={p.column.key === filterValue}
                    className={"filterColumn"}
                    defaultValue={filters[p.column.key]}
                    onChange={(e) => {
                      setTimeout(() => {
                        setFilters({
                          ...filters,
                          [p.column.key]: e.target.value,
                        });
                        setFilterValue(p.column.key);
                      }, 500);
                    }}
                    onKeyDown={inputStopPropagation}
                  />
                )}
              {p.column.searchable && p.column.type === "dropdown" && (
                <select
                  className={"filterColumn"}
                  value={filters[p.column.key]}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      [p.column.key]: e.target.value,
                    })
                  }
                  onKeyDown={selectStopPropagation}
                >
                  <option value="">{"All"}</option>
                  {p.column.filters.map((f: any) => {
                    return (
                      <option value={f.id} key={"f-" + f.id}>
                        {f.name}
                      </option>
                    );
                  })}
                </select>
              )}
              <div
                style={{
                  position: "absolute",
                  top: -3,
                  right: -8,
                  height: 20,
                  display: "flex",
                  alignItems: "center",
                  zIndex: -9,
                }}
              >
                <>
                  <IconDesc
                    style={{ position: "relative", right: -24, top: -5 }}
                    className={
                      "sortingIcon " +
                      (sortColumns.length > 0 &&
                        sortColumns[0].columnKey === p.column.key &&
                        sortColumns[0].direction === "ASC" &&
                        "active")
                    }
                  />
                  <IconAsc
                    className={
                      "sortingIcon " +
                      (sortColumns.length > 0 &&
                        sortColumns[0].columnKey === p.column.key &&
                        sortColumns[0].direction === "DESC" &&
                        "active")
                    }
                  />
                </>
              </div>
            </div>
          );
        };
      }
      if (i === 1) {
        c.renderSummaryCell = ({ row }) => {
          return (
            <strong style={{ fontWeight: 500 }}>
              {"Total: " + row.totalCount + " Records"}
            </strong>
          );
        };
      }
      return c;
    });
  };

  const {
    childRef,
    cols,
    rows,
    exportOption,
    exportName,
    height,
    noHeight,
    width,
    headerRowHeight,
    onReload,
    onAdd,
    getDefaultNewRow,
  } = props;
  const [gridCols, setGridCols] = useState<any>(generateCols(cols));
  // const gridCols = useMemo(() => generateCols(cols), [cols]);
  const [gridRows, setGridRows] = useState<any>(rows);
  const [rowHeight, setRowHeight] = useState(props.rowHeight || 50);
  const [sortColumns, setSortColumns] = useState<readonly SortColumn[]>([]);
  const onSortColumnsChange = useCallback((sortColumns: SortColumn[]) => {
    setSortColumns(sortColumns.slice(-1));
  }, []);
  const [selectedRows, setSelectedRows] = useState<any>(
    (): ReadonlySet<string> => new Set()
  );
  const [filters, setFilters] = useState<any>(() => ({
    enabled: true,
  }));
  const filteredRows = useMemo(() => {
    return (
      rows &&
      rows.filter((r) => {
        let rslt = true;
        for (let key in filters) {
          if (key == "enabled") {
            continue;
          }
          if (!r[key]) {
            r[key] = "";
          }
          rslt =
            rslt &&
            r[key]
              .toString()
              .toUpperCase()
              .includes(filters[key].toString().toUpperCase());
        }
        return rslt;
      })
    );
  }, [rows, filters]);

  useEffect(() => {
    return () => {
      // setGridCols(cols);
      // setGridRows(rows);
    };
  }, []);

  let colJson = {};
  for (let i in gridCols) {
    colJson[gridCols[i].key] = gridCols[i].type;
  }

  type Comparator = (a: any, b: any) => number;

  const getComparator = (sortColumn: string, sortInfo: any): Comparator => {
    switch (colJson[sortColumn]) {
      case "dropdown":
      case "text":
      case "date":
        return (a, b) => {
          return a[sortColumn].localeCompare(b[sortColumn]);
        };
      case "boolean":
        return (a, b) => {
          return a[sortColumn] === b[sortColumn] ? 0 : a[sortColumn] ? 1 : -1;
        };
      case "number":
        return (a, b) => {
          return a[sortColumn] - b[sortColumn];
        };
      default:
        throw new Error(`unsupported sortColumn: "${sortColumn}"`);
    }
  };

  const sortedRows = useMemo((): readonly any[] => {
    if (sortColumns.length === 0) return filteredRows;
    return [...filteredRows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey, sort);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === "ASC" ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [filteredRows, sortColumns]);

  if (childRef) {
    childRef.getRowData = () => {
      return gridRows;
    };
    childRef.setData = (rows) => {
      setGridRows(rows);
    };
  }

  const summaryRows = useMemo(() => {
    return [
      {
        id: "total_0",
        totalCount: sortedRows.length,
        // yesCount: rows.filter((r) => r.available).length
      },
    ];
  }, [sortedRows]);

  // if (filters["permitNo"]) {
  //   inputRef["permitNo"].focus();
  // }

  const gridElement = (
    <FilterContext.Provider value={filters}>
      <DataGrid
        rowKeyGetter={(row: any) => {
          return row.id;
        }}
        style={{
          width: width || "100%",
          maxHeight: height ? height : noHeight ? "auto" : 400,
          height: "auto",
          fontSize: "0.8rem",
          fontWeight: "300",
        }}
        bottomSummaryRows={summaryRows}
        // headerRowHeight={headerRowHeight}
        rowHeight={rowHeight}
        columns={gridCols}
        rows={sortedRows}
        sortColumns={sortColumns}
        defaultColumnOptions={{
          resizable: true,
        }}
        direction={"ltr"}
        summaryRowHeight={30}
        onSortColumnsChange={onSortColumnsChange}
        onRowsChange={setGridRows}
        onSelectedRowsChange={setSelectedRows}
        onCellClick={(args, event) => {
          // console.log("onCellClick: ", args, event);
          // if (args.column.key === "title") {
          //   event.preventGridDefault();
          //   args.selectCell(true);
          // }
        }}
      />
    </FilterContext.Provider>
  );

  const insertNewRow = () => {
    const newRow: any = getDefaultNewRow();
    setGridRows([...gridRows, newRow]);
  };

  console.log("excelGrid: ", gridCols, sortedRows, filters);

  return (
    <div className="excelGrid">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 15,
          marginTop: 10,
        }}
      >
        <div className="excelAddBtn">
          {onAdd && (
            <Btn
              text={LABELS.ADD_NEW}
              marginRight={15}
              onClick={onAdd}
              bgColor={COLORS.GRID_ADD_NEW}
            />
          )}
          {onReload && (
            <Btn
              text={LABELS.RELOAD}
              marginRight={15}
              onClick={onReload}
              bgColor={COLORS.RELOAD_EXPORT}
              color={COLORS.RELOAD_EXPORT_TEXT}
            />
          )}
          {getDefaultNewRow && (
            <Btn text={LABELS.ADD_NEW_ROW} onClick={insertNewRow} />
          )}
        </div>

        {exportOption && (
          <div className="exportOptions">
            <Btn
              text={"Export PDF"}
              marginRight={15}
              onClick={() => exportToPdf(gridElement, exportName + ".pdf")}
              bgColor={COLORS.RELOAD_EXPORT}
              color={COLORS.RELOAD_EXPORT_TEXT}
            />
            <Btn
              text={"Export CSV"}
              onClick={() => exportToCsv(gridElement, exportName + ".csv")}
              bgColor={COLORS.RELOAD_EXPORT}
              color={COLORS.RELOAD_EXPORT_TEXT}
            />
          </div>
        )}
      </div>
      {gridElement}
    </div>
  );
};

export default ExcelGrid;
